// SLICE-REFERRAL — owner Referrals page. Shows the entitlement state, the
// per-program referral config form (saved via the program PATCH, which the API
// gates on the tenant entitlement + validates against the card size), and
// tenant-wide counts. Lives in the More sheet (primary: false).

import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  DEFAULT_REFERRAL_CONFIG,
  REFERRAL_MAX_STAMPS,
  REFERRAL_MIN_STAMPS,
  type ReferralConfig,
} from '@naya/shared'

import { ownerApi, OwnerApiError } from '../ownerApi.js'
import {
  referralOverviewKey,
  useReferralOverview,
} from '../hooks/useReferralOverview.js'
import { usePrograms } from '../program/hooks/useProgram.js'
import './referralsPage.css'

const DEFAULT_CONFIG = DEFAULT_REFERRAL_CONFIG

export function ReferralsPage() {
  const qc = useQueryClient()
  const [form, setForm] = useState<ReferralConfig>(DEFAULT_CONFIG)

  // Multi-program tenants pick which program's referral to configure (mirrors
  // the Broadcasts picker — surfaced only when >1 active loyalty program).
  const programsQ = usePrograms()
  const activePrograms = (programsQ.data?.programs ?? []).filter(
    (p) => p.status === 'active' && p.pass_type === 'loyalty',
  )
  const multiProgram = activePrograms.length > 1
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null)

  const query = useReferralOverview(selectedProgramId)
  const data = query.data
  const programId = data?.program_id ?? null
  const totalStamps = data?.total_stamps ?? REFERRAL_MAX_STAMPS
  const stampMax = Math.min(REFERRAL_MAX_STAMPS, totalStamps)

  // Re-seed the form whenever the resolved program changes (switching programs
  // or first load) — falling back to defaults for a program with no config.
  useEffect(() => {
    setForm(data?.config ?? DEFAULT_CONFIG)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.program_id])

  const save = useMutation({
    mutationFn: (config: ReferralConfig) =>
      ownerApi.patch(`/owner/program/${programId}`, { referral_config: config }),
    onSuccess: () => {
      toast.success('Referral settings saved')
      void qc.invalidateQueries({ queryKey: referralOverviewKey })
    },
    onError: (err) => {
      toast.error(
        err instanceof OwnerApiError
          ? err.userMessage
          : 'Could not save referral settings',
      )
    },
  })

  if (query.isLoading) {
    return <div className="owner-referral owner-referral-loading">Loading…</div>
  }
  if (query.isError || !data) {
    return (
      <div className="owner-referral owner-referral-loading">
        Could not load referrals. Pull to refresh.
      </div>
    )
  }

  if (!data.entitled) {
    return (
      <div className="owner-referral">
        <header className="owner-referral-header">
          <h1>Referrals</h1>
          <p className="owner-referral-subtitle">
            Turn your regulars into your best marketers — let a customer refer a
            friend straight from their wallet pass.
          </p>
        </header>
        <div className="owner-referral-locked">
          Referrals aren’t switched on for your account yet. Message Naya support
          and we’ll enable it for you.
        </div>
      </div>
    )
  }

  function setNum(key: keyof ReferralConfig, value: number) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  // Disable Save unless something actually changed vs the saved config. This is
  // what makes the toggle behave: with the program already off and untouched
  // there is nothing to save (button stays disabled), but flipping the toggle
  // off on a running program is a real change — Save lights up so the owner can
  // persist the disable. (Turning referral off IS done by saving enabled:false;
  // the button must not be hard-disabled just because the toggle is off.)
  const savedConfig = data.config ?? DEFAULT_CONFIG
  const dirty =
    form.enabled !== savedConfig.enabled ||
    form.referee_welcome_multiplier !== savedConfig.referee_welcome_multiplier ||
    form.referee_threshold_stamps !== savedConfig.referee_threshold_stamps ||
    form.referrer_bonus_stamps !== savedConfig.referrer_bonus_stamps ||
    form.max_referrals_per_person !== savedConfig.max_referrals_per_person

  return (
    <div className="owner-referral">
      <header className="owner-referral-header">
        <h1>Referrals</h1>
        <p className="owner-referral-subtitle">
          A customer refers a friend. The friend’s first stamp counts double, and
          once they reach your goal, the referrer earns bonus stamps.
        </p>
      </header>

      {multiProgram && (
        <div className="owner-referral-field" style={{ marginBottom: 20 }}>
          <label className="owner-referral-label" htmlFor="referral-program">
            Program
          </label>
          <select
            id="referral-program"
            className="owner-referral-input"
            value={programId ?? ''}
            onChange={(e) => setSelectedProgramId(e.target.value)}
          >
            {activePrograms.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
          <p className="owner-referral-hint">
            Referral settings and codes are per program. Pick which card this
            applies to.
          </p>
        </div>
      )}

      {!data.master_enabled && (
        <div className="owner-referral-note">
          Referrals are enabled for your account but not live yet — they’ll start
          working shortly. You can set them up now.
        </div>
      )}

      <section className="owner-referral-stats">
        <Stat label="Codes issued" value={data.counts.codes_issued} />
        <Stat label="Friends joined" value={data.counts.friends_joined} />
        <Stat label="Rewards paid" value={data.counts.rewards_paid} />
        <Stat label="In progress" value={data.counts.pending} />
      </section>

      {programId === null ? (
        <div className="owner-referral-locked">
          Create and activate a loyalty program first, then come back to set up
          referrals.
        </div>
      ) : (
        <form
          className="owner-referral-form"
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate(form)
          }}
        >
          <label className="owner-referral-toggle">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) =>
                setForm((f) => ({ ...f, enabled: e.target.checked }))
              }
            />
            <span>Run a referral program</span>
          </label>

          <fieldset
            className="owner-referral-fields"
            disabled={!form.enabled}
          >
            <Field
              label="Welcome bonus (friend’s first stamp)"
              hint="The friend’s very first stamp counts this many times."
            >
              <select
                className="owner-referral-input"
                value={form.referee_welcome_multiplier}
                onChange={(e) =>
                  setNum('referee_welcome_multiplier', Number(e.target.value))
                }
              >
                <option value={2}>2× (double)</option>
                <option value={3}>3× (triple)</option>
              </select>
            </Field>

            <Field
              label="Friend’s stamp goal"
              hint={`Stamps the friend must earn before the referrer is paid (max ${stampMax}).`}
            >
              <input
                type="number"
                className="owner-referral-input"
                min={REFERRAL_MIN_STAMPS}
                max={stampMax}
                value={form.referee_threshold_stamps}
                onChange={(e) =>
                  setNum('referee_threshold_stamps', Number(e.target.value))
                }
              />
            </Field>

            <Field
              label="Bonus stamps for the referrer"
              hint={`Stamps the referrer earns per successful referral (max ${stampMax}).`}
            >
              <input
                type="number"
                className="owner-referral-input"
                min={REFERRAL_MIN_STAMPS}
                max={stampMax}
                value={form.referrer_bonus_stamps}
                onChange={(e) =>
                  setNum('referrer_bonus_stamps', Number(e.target.value))
                }
              />
            </Field>

            <Field
              label="Lifetime cap per customer"
              hint="Most referrals one customer can be rewarded for."
            >
              <input
                type="number"
                className="owner-referral-input"
                min={1}
                max={1000}
                value={form.max_referrals_per_person}
                onChange={(e) =>
                  setNum('max_referrals_per_person', Number(e.target.value))
                }
              />
            </Field>
          </fieldset>

          <button
            type="submit"
            className="owner-referral-save"
            disabled={save.isPending || !dirty}
          >
            {save.isPending ? 'Saving…' : 'Save referral settings'}
          </button>
        </form>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="owner-referral-stat">
      <div className="owner-referral-stat-value">{value}</div>
      <div className="owner-referral-stat-label">{label}</div>
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint: string
  children: React.ReactNode
}) {
  return (
    <div className="owner-referral-field">
      <label className="owner-referral-label">{label}</label>
      {children}
      <p className="owner-referral-hint">{hint}</p>
    </div>
  )
}
